import React, { useState, useEffect } from 'react';
import { Typography, Button, Input, Alert, IconButton, Select, Option } from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import CookingLittlePerson from './svg/CookingLittlePerson';
import Cooking from './svg/Cooking';
import Save from './svg/Save';
import { brandLogo as logo, brandName, mcDePageClass } from '../utils/branding';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';

const Login = ({ onLogin }) => {
    const navigate = useNavigate();
    const [currentScreen, setCurrentScreen] = useState(0);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState(''); // Will be set based on browser language

    // List of common country codes with flags
    const countryCodes = [
        { code: '33', country: 'France', flag: '🇫🇷' },
        { code: '31', country: 'Netherlands', flag: '🇳🇱' },
        { code: '32', country: 'Belgium', flag: '🇧🇪' },
        { code: '49', country: 'Germany', flag: '🇩🇪' },
        { code: '44', country: 'United Kingdom', flag: '🇬🇧' },
        { code: '34', country: 'Spain', flag: '🇪🇸' },
        { code: '39', country: 'Italy', flag: '🇮🇹' },
        { code: '1', country: 'United States', flag: '🇺🇸' },
        { code: '41', country: 'Switzerland', flag: '🇨🇭' },
        { code: '351', country: 'Portugal', flag: '🇵🇹' },
        { code: '352', country: 'Luxembourg', flag: '🇱🇺' },
        { code: '45', country: 'Denmark', flag: '🇩🇰' },
        { code: '46', country: 'Sweden', flag: '🇸🇪' },
        { code: '47', country: 'Norway', flag: '🇳🇴' },
        { code: '358', country: 'Finland', flag: '🇫🇮' },
        { code: '43', country: 'Austria', flag: '🇦🇹' },
        { code: '48', country: 'Poland', flag: '🇵🇱' },
        { code: '420', country: 'Czech Republic', flag: '🇨🇿' },
        { code: '36', country: 'Hungary', flag: '🇭🇺' },
        { code: '30', country: 'Greece', flag: '🇬🇷' },
        { code: '353', country: 'Ireland', flag: '🇮🇪' },
        { code: '40', country: 'Romania', flag: '🇷🇴' },
        { code: '359', country: 'Bulgaria', flag: '🇧🇬' },
        { code: '385', country: 'Croatia', flag: '🇭🇷' },
        { code: '386', country: 'Slovenia', flag: '🇸🇮' },
        { code: '421', country: 'Slovakia', flag: '🇸🇰' },
        { code: '370', country: 'Lithuania', flag: '🇱🇹' },
        { code: '371', country: 'Latvia', flag: '🇱🇻' },
        { code: '372', country: 'Estonia', flag: '🇪🇪' },
        { code: '356', country: 'Malta', flag: '🇲🇹' },
        { code: '357', country: 'Cyprus', flag: '🇨🇾' },
        { code: '212', country: 'Morocco', flag: '🇲🇦' },
        { code: '213', country: 'Algeria', flag: '🇩🇿' },
        { code: '216', country: 'Tunisia', flag: '🇹🇳' },
        { code: '20', country: 'Egypt', flag: '🇪🇬' },
        { code: '971', country: 'United Arab Emirates', flag: '🇦🇪' },
        { code: '973', country: 'Bahrain', flag: '🇧🇭' },
        { code: '966', country: 'Saudi Arabia', flag: '🇸🇦' },
        { code: '965', country: 'Kuwait', flag: '🇰🇼' },
        { code: '974', country: 'Qatar', flag: '🇶🇦' },
        { code: '968', country: 'Oman', flag: '🇴🇲' },
        { code: '964', country: 'Iraq', flag: '🇮🇶' },
        { code: '962', country: 'Jordan', flag: '🇯🇴' },
        { code: '961', country: 'Lebanon', flag: '🇱🇧' },
        { code: '963', country: 'Syria', flag: '🇸🇾' },
        { code: '970', country: 'Palestine', flag: '🇵🇸' },
        { code: '967', country: 'Yemen', flag: '🇾🇪' },
        { code: '27', country: 'South Africa', flag: '🇿🇦' },
    ];
    const [loginMethod, setLoginMethod] = useState('credentials');
    const [rememberMe, setRememberMe] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { language } = useLanguage();

    // Map befr to french and benl to nederlands for translations
    let translationLanguage = language;
    if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
    if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
    if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
    if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
    if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
    if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
    if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
    if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
    if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
    if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
    if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
    if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
    if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
    if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
    if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
    if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
    if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
    if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
    if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
    if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
    if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
    if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
    if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
    if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
    if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
    if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
    if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
    if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
    if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

    const t = languages[translationLanguage] || languages['english']; // Fallback to English if translation not found

    const introScreens = [
        {
            title: t.budgetFriendlyCooking,
            description: t.budgetFriendlyDescription,
            SvgComponent: Cooking
        },
        {
            title: t.customRecipeSuggestions,
            description: t.customRecipeDescription,
            SvgComponent: CookingLittlePerson
        },
        {
            title: t.secureAndEasy,
            description: t.secureAndEasyDescription,
            SvgComponent: Save
        },
        {
            title: t.logIn,
            isLoginScreen: true
        }
    ];

    // Function to get country code based on selected language (from LanguageContext)
    const getCountryCodeFromLanguage = (selectedLanguage) => {
        // Map of language codes to international dialing codes
        const languageToDialingCode = {
            'french': '33',
            'nederlands': '31',
            'be-fr': '32',    // Belgian French
            'be-nl': '32',    // Belgian Dutch
            'deutsch': '49',
            'english': '44',  // Default to UK
            'spanish': '34',
            'italian': '39',
            'portuguese': '351',
            'polish': '48',
            'norwegian': '47',
            'serbian': '381',
            'romanian': '40',
            // Arabic countries
            'bh-ar': '973',   // Bahrain Arabic
            'bh-en': '973',   // Bahrain English
            'bahrain-arabic': '973',   // Bahrain Arabic (alternative)
            'bahrain-english': '973',  // Bahrain English (alternative)
            'uae-arabic': '971',  // UAE Arabic
            'uae-english': '971', // UAE English
            'sa-arabic': '966',   // Saudi Arabia Arabic
            'sa-english': '966',  // Saudi Arabia English
            'kw-arabic': '965',   // Kuwait Arabic
            'kw-english': '965',  // Kuwait English
            'qa-arabic': '974',   // Qatar Arabic
            'qa-english': '974',  // Qatar English
            'om-arabic': '968',   // Oman Arabic
            'om-english': '968',  // Oman English
            'iq-arabic': '964',   // Iraq Arabic
            'iq-english': '964',  // Iraq English
            'jo-arabic': '962',   // Jordan Arabic
            'jo-english': '962',  // Jordan English
            'lb-arabic': '961',   // Lebanon Arabic
            'lb-english': '961',  // Lebanon English
            'sy-arabic': '963',   // Syria Arabic
            'sy-english': '963',  // Syria English
            'ps-arabic': '970',   // Palestine Arabic
            'ps-english': '970',  // Palestine English
            'ye-arabic': '967',   // Yemen Arabic
            'ye-english': '967',  // Yemen English
            'eg-arabic': '20',    // Egypt Arabic
            'eg-english': '20',   // Egypt English
            'arabic': '971',      // Generic Arabic (default to UAE)
        };

        // First try to match the exact language code
        if (languageToDialingCode[selectedLanguage]) {
            return languageToDialingCode[selectedLanguage];
        }

        // Fallback: try to get from browser language if no match
        const browserLanguage = navigator.language || navigator.userLanguage;
        const languageCode = browserLanguage.split('-')[0];
        const countryCode = browserLanguage.split('-')[1]?.toUpperCase();

        const browserLanguageToDialingCode = {
            'fr': '33',
            'nl': '31',
            'be': '32',
            'de': '49',
            'en': {
                'GB': '44',
                'US': '1',
                'default': '44'
            },
            'es': '34',
            'it': '39',
            'pt': '351',
            'pl': '48',
            'no': '47',
            'ae': '971',
            'bh': '973',
            'sa': '966',
            'kw': '965',
            'qa': '974',
            'om': '968',
            'iq': '964',
            'jo': '962',
            'lb': '961',
            'sy': '963',
            'ps': '970',
            'ye': '967',
            'eg': '20',
        };

        if (languageCode === 'en' && countryCode) {
            return browserLanguageToDialingCode[languageCode]?.[countryCode] ||
                browserLanguageToDialingCode[languageCode]?.['default'] || '44';
        }

        if (browserLanguageToDialingCode[languageCode]) {
            return browserLanguageToDialingCode[languageCode];
        }

        if (countryCode && browserLanguageToDialingCode[countryCode.toLowerCase()]) {
            return browserLanguageToDialingCode[countryCode.toLowerCase()];
        }

        // Default to France if no match
        return '33';
    };

    // Update country code when language changes
    useEffect(() => {
        const countryCodeFromLang = getCountryCodeFromLanguage(language);
        setCountryCode(countryCodeFromLang);
    }, [language]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('s');
        const msisdn = params.get('m');

        // If s= or m= is in URL, clear all cookies and localStorage to prevent logging in with old/other account
        if (sessionId || msisdn) {
            console.log('Session or MSISDN parameter detected, clearing all cookies and localStorage');
            
            // Clear all cookies
            const allCookies = document.cookie.split(';');
            allCookies.forEach(cookie => {
                const eqPos = cookie.indexOf('=');
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                if (name) {
                    // Remove cookie for current path
                    Cookies.remove(name);
                    // Also try to remove for root path
                    Cookies.remove(name, { path: '/' });
                    // Also try to remove for all possible paths
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
                }
            });
            
            // Clear all localStorage
            localStorage.clear();
            
            console.log('All cookies and localStorage cleared');
        }

        const hasSkipped = Cookies.get('skippedIntro') === 'true';

        // Set default country code based on selected language
        const countryCodeFromLang = getCountryCodeFromLanguage(language);
        setCountryCode(countryCodeFromLang);

        if (params.get('skip') === 'true' || hasSkipped) {
            setCurrentScreen(3);
        }

        if (sessionId) {
            checkSession(sessionId);
        }

        if (msisdn) {
            setLoginMethod('phone');
            setPhoneNumber(msisdn);
            setCurrentScreen(3); // Ga direct naar login scherm
        }
    }, []);

    const checkSession = async (sessionId) => {
        try {
            console.log('Starting session check for:', sessionId);
            const response = await fetch(`/api/login/check-session?sessionId=${sessionId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            console.log('Response received:', {
                ok: response.ok,
                status: response.status,
                statusText: response.statusText
            });

            const data = await response.json();
            console.log('Session check response data:', data);
            console.log('Checking conditions:', {
                responseOk: response.ok,
                isValid: data.isValid,
                userId: data.userId,
                allConditionsMet: response.ok && data.isValid && data.userId
            });

            if (response.ok && data.isValid && data.userId) {
                console.log('Session is valid, logging in user with userId:', data.userId);
                // Only userId is required - username and email are optional
                localStorage.setItem('userId', String(data.userId));
                
                // Store username only if available (optional)
                if (data.username) {
                    localStorage.setItem('username', String(data.username));
                }
                
                // Store optional account session data
                if (data.accountSessionId) {
                    localStorage.setItem('accountSessionId', String(data.accountSessionId));
                    localStorage.setItem('sessionId', String(data.accountSessionId)); // Also store as sessionId for cancel subscription
                }
                if (data.paymentMethod) {
                    localStorage.setItem('paymentMethod', String(data.paymentMethod));
                }
                
                console.log('Setting login cookie...');
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 }); // 1 day
                    console.log('Cookie set successfully');
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    // Try with simpler options
                    Cookies.set('isLoggedIn', 'true');
                }
                console.log('Cookie set, calling onLogin()...');
                onLogin();
                console.log('onLogin() called successfully');
                // Navigate to home page after successful login
                navigate('/', { replace: true });
            } else if (data.redirectToSignup) {
                // User has active subscription but doesn't exist in database yet
                // Redirect to signup page with session ID
                console.log('Session is valid but user not found, redirecting to signup');
                navigate(`/signup?s=${sessionId}`);
            } else {
                console.log('Login conditions not met:', {
                    responseOk: response.ok,
                    isValid: data.isValid,
                    userId: data.userId,
                    redirectToSignup: data.redirectToSignup
                });
                setShowAlert(true);
                setAlertMessage(t.sessionExpired || 'Session expired. Please log in again.');
                setTimeout(() => setShowAlert(false), 3000);
            }
        } catch (error) {
            console.error('Session check error:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            setShowAlert(true);
            setAlertMessage(t.sessionCheckError || 'Error checking session. Please try logging in.');
            setTimeout(() => setShowAlert(false), 3000);
        }
    };

    const handleNext = () => {
        if (currentScreen < introScreens.length - 1) {
            setCurrentScreen(currentScreen + 1);
        }
    };

    const handleSkip = () => {
        Cookies.set('skippedIntro', 'true', { expires: 365 });
        setCurrentScreen(introScreens.length - 1);
    };

    const handleLoginMethodSwitch = () => {
        setLoginMethod(loginMethod === 'credentials' ? 'phone' : 'credentials');
        setUsername('');
        setPassword('');
        setPhoneNumber('');
    };

    const handlePhoneLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            // Format phone number with international prefix
            const formattedPhoneNumber = countryCode + phoneNumber.replace(/^0+/, '');

            const response = await fetch('/api/login/phone', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phoneNumber: formattedPhoneNumber
                }),
            });

            const data = await response.json();

            if (response.ok && data.isSubscribed) {
                // Check if user needs to be redirected to signup
                if (data.redirectToSignup) {
                    console.log('User has valid MSISDN but no account, redirecting to signup');
                    // Store session ID if available for signup
                    if (data.accountSessionId) {
                        localStorage.setItem('sessionId', data.accountSessionId);
                    }
                    // Navigate to signup with phone number and session ID
                    const signupUrl = data.accountSessionId 
                        ? `/signup?msisdn=${formattedPhoneNumber}&s=${data.accountSessionId}`
                        : `/signup?msisdn=${formattedPhoneNumber}`;
                    setIsLoading(false);
                    navigate(signupUrl);
                    return;
                }

                // User exists, proceed with login
                // Only userId is required - username and email are optional
                if (data.userId !== undefined && data.userId !== null) {
                    localStorage.setItem('userId', String(data.userId));
                } else {
                    console.error('No userId in response, cannot proceed with login');
                    setIsLoading(false);
                    setShowAlert(true);
                    setAlertMessage(t.loginError || t.defaultLoginError);
                    setTimeout(() => setShowAlert(false), 3000);
                    return;
                }

                // Store optional data
                if (phoneNumber) {
                    localStorage.setItem('phoneNumber', String(phoneNumber));
                }
                if (data.username) {
                    localStorage.setItem('username', String(data.username));
                }
                if (data.accountSessionId) {
                    localStorage.setItem('accountSessionId', String(data.accountSessionId));
                    localStorage.setItem('sessionId', String(data.accountSessionId)); // Also store as sessionId for cancel subscription
                }
                if (data.paymentMethod) {
                    localStorage.setItem('paymentMethod', String(data.paymentMethod));
                }

                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 }); // 1 day
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    Cookies.set('isLoggedIn', 'true');
                }
                setIsLoading(false);
                onLogin();
            } else {
                setIsLoading(false);
                setShowAlert(true);
                setAlertMessage(data.error || t.loginError || t.defaultLoginError);
                setTimeout(() => setShowAlert(false), 3000);
            }
        } catch (error) {
            console.error('Phone login error:', error);
            setIsLoading(false);
            setShowAlert(true);
            setAlertMessage(t.loginError || t.defaultLoginError);
            setTimeout(() => setShowAlert(false), 3000);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username,
                    password
                }),
            });

            const data = await response.json();

            if (response.ok && data.isSubscribed) {
                // Only userId is required - username and email are optional
                if (data.userId !== undefined && data.userId !== null) {
                    localStorage.setItem('userId', String(data.userId));
                } else {
                    console.error('No userId in response, cannot proceed with login');
                    setIsLoading(false);
                    setShowAlert(true);
                    setAlertMessage(data.error || t.loginError || t.defaultLoginError);
                    setTimeout(() => setShowAlert(false), 3000);
                    return;
                }

                // Store optional username if available
                if (data.username) {
                    localStorage.setItem('username', String(data.username));
                } else if (username) {
                    // Fallback to the username used for login
                    localStorage.setItem('username', String(username));
                }

                // Store optional account session data
                if (data.accountSessionId) {
                    localStorage.setItem('accountSessionId', String(data.accountSessionId));
                    localStorage.setItem('sessionId', String(data.accountSessionId)); // Also store as sessionId for cancel subscription
                }
                if (data.paymentMethod) {
                    localStorage.setItem('paymentMethod', String(data.paymentMethod));
                }

                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 }); // 1 day
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    Cookies.set('isLoggedIn', 'true');
                }
                setIsLoading(false);
                onLogin();
            } else {
                setIsLoading(false);
                setShowAlert(true);
                setAlertMessage(data.error || t.loginError || t.defaultLoginError);
                setTimeout(() => setShowAlert(false), 3000);
            }
        } catch (error) {
            console.error('Login error:', error);
            setIsLoading(false);
            setShowAlert(true);
            setAlertMessage(t.loginError || t.defaultLoginError);
            setTimeout(() => setShowAlert(false), 3000);
        }
    };

    const renderLoginForm = () => {
        // Check if current language is Arabic
        const isArabic = translationLanguage === 'arabic';
        
        if (loginMethod === 'phone') {
            return (
                <form onSubmit={handlePhoneLogin} className="w-full max-w-sm flex flex-col gap-2">
                    <div className="flex gap-2 mb-4">
                        <div className="w-1/3">
                            <Select
                                label="Country"
                                value={countryCode}
                                onChange={(value) => setCountryCode(value)}
                                className={`bg-white ${isArabic ? 'arabic-select' : ''}`}
                                disabled={isLoading}
                            >
                                {countryCodes.map((country) => (
                                    <Option key={country.code} value={country.code}>
                                        <div className="flex items-center gap-2" style={isArabic ? { zIndex: 999, right: '40px' } : {}}>
                                            <span>{country.flag}</span>
                                            <span>+{country.code}</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        <div className="w-2/3">
                            <Input
                                type="tel"
                                label={t.mobileNumber}
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                className="bg-white"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <Button type="submit" className="w-full mb-4 flex items-center justify-center gap-2" color="green" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t.signingIn || 'Signing in...'}
                            </>
                        ) : (
                            t.logIn
                        )}
                    </Button>
                    {translationLanguage === 'polish' && (
                        <div className="flex flex-col items-center">
                            <Typography variant="small" className="text-center text-gray-700 font-medium uppercase">
                                {t.orClickHere || 'Lub kliknij tutaj'}
                            </Typography>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-gray-500 animate-bounce">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                            </svg>
                        </div>
                    )}
                    <Button
                        onClick={handleLoginMethodSwitch}
                        variant="text"
                        color="blue-gray"
                        className="normal-case"
                        disabled={isLoading}
                    >
                        🔄 {t.usernameOrMobile}
                    </Button>
                </form>
            );
        }

        return (
            <form onSubmit={handleLogin} className="w-full max-w-sm flex flex-col gap-2">
                <Input
                    label={t.username}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="mb-4 bg-white"
                    required
                    disabled={isLoading}
                />
                <Input
                    type="password"
                    label={t.password}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mb-4 bg-white"
                    required
                    disabled={isLoading}
                />
                <Button type="submit" className="w-full mb-4 flex items-center justify-center gap-2" color="green" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t.signingIn || 'Signing in...'}
                        </>
                    ) : (
                        t.logIn
                    )}
                </Button>
                {translationLanguage === 'polish' && (
                    <div className="flex flex-col items-center">
                        <Typography variant="small" className="text-center text-gray-700 font-medium uppercase">
                            {t.orClickHere || 'Lub kliknij tutaj'}
                        </Typography>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-gray-500 animate-bounce">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                        </svg>
                    </div>
                )}
                <Button
                    onClick={handleLoginMethodSwitch}
                    variant="text"
                    color="blue-gray"
                    className="normal-case"
                    disabled={isLoading}
                >
                    🔄 {t.usernameOrMobile}
                </Button>
            </form>
        );
    };

    const renderScreen = (screen, index) => {
        if (screen.isLoginScreen) {
            return (
                <div key={index} className={`flex flex-col items-center justify-center min-h-screen p-4 gap-2 ${mcDePageClass(language)}`}>
                    <img src={logo} alt={brandName} className="h-24" />
                    <Typography variant="h4" className="mb-4">{screen.title}</Typography>
                    {renderLoginForm()}
                </div>
            );
        }

        const SvgComponent = screen.SvgComponent;

        return (
            <div key={index} className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <img src={logo} alt={brandName} className="h-16 mb-4" />
                <div className="mb-4">
                    <SvgComponent />
                </div>
                <Typography variant="h4" className="mb-2">{screen.title}</Typography>
                <Typography variant="paragraph" className="mb-8 text-center">{screen.description}</Typography>
                <div className='flex flex-col items-center justify-center w-full p-4 gap-4'>
                    <Button onClick={handleNext} className="w-full rounded-full" color="green">
                        {t.next}
                    </Button>
                    <Button onClick={handleSkip} variant="outlined" className="w-full rounded-full" color="green">
                        {t.skip}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className={mcDePageClass(language)}>
            {renderScreen(introScreens[currentScreen], currentScreen)}
            {showAlert && (
                <div className="fixed bottom-4 left-12 right-12 z-50">
                    <Alert
                        open={showAlert}
                        color="red"
                        className="max-w-screen-md mx-auto flex justify-between items-center"
                        animate={{
                            mount: { y: 0 },
                            unmount: { y: 100 },
                        }}
                        action={
                            <IconButton
                                variant="text"
                                color="white"
                                size="sm"
                                onClick={() => setShowAlert(false)}
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </IconButton>
                        }
                    >
                        {alertMessage}
                    </Alert>
                </div>
            )}
        </div>
    );
};

export default Login;
